import { useMutation, useQueryClient } from 'react-query'
import { useAuth0 } from '../../../contexts/auth0-context'
import endpoints from '../../../API/endpoints'
import callSecureAPI from '../../../API/callSecureAPI'
import { Comment } from '../../../shared/components/Comments/Comments.types'
import { CommentsViewComment, UpdateCommentsViewCommentPayload } from '../CommentsView.types'
import { getCommentsDashboardQueryKey } from '../queries/useGetCommentsDashboardQuery'
import { getCommentsQueryKey } from '../../../shared/components/Comments/useCommentsQueries'

const getNormalizedUpdates = (
    updates: Partial<Omit<UpdateCommentsViewCommentPayload, 'commentId' | 'domainTabId'>>
) => {
    const normalizedUpdates: Partial<Omit<UpdateCommentsViewCommentPayload, 'commentId' | 'domainTabId'>> = {}

    if ('action' in updates) normalizedUpdates.action = updates.action?.trim() ?? ''
    if ('isResolved' in updates) normalizedUpdates.isResolved = updates.isResolved

    return normalizedUpdates
}

export const useUpdateCommentsViewCommentMutation = (strategyId?: string) => {
    const queryClient = useQueryClient()
    const { getTokenSilently } = useAuth0()

    const updateComment = async ({
        commentId,
        domainTabId: _domainTabId,
        ...updates
    }: UpdateCommentsViewCommentPayload) => {
        const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
        const { url, method } = endpoints.updateDataComment

        const response = await callSecureAPI(url + commentId, method, token, getNormalizedUpdates(updates))

        if (!response?.success) {
            throw new Error(response?.error || 'Failed to update comment')
        }

        return response?.data as Partial<CommentsViewComment> & Pick<CommentsViewComment, '_id'>
    }

    return useMutation<
        Partial<CommentsViewComment> & Pick<CommentsViewComment, '_id'>,
        Error,
        UpdateCommentsViewCommentPayload
    >(updateComment, {
        onSuccess: (updatedComment) => {
            if (!strategyId) {
                return
            }

            queryClient.setQueryData<CommentsViewComment[] | undefined>(
                getCommentsDashboardQueryKey(strategyId),
                (currentComments) =>
                    currentComments?.map((comment) =>
                        comment._id === updatedComment._id ? { ...comment, ...updatedComment } : comment
                    )
            )

            queryClient.setQueryData<Comment[]>(
                getCommentsQueryKey(updatedComment.dataId, updatedComment.dataType),
                (currentComments) =>
                    currentComments?.map((comment) =>
                        comment._id === updatedComment._id ? { ...comment, ...updatedComment } : comment
                    ) ?? []
            )
        },
    })
}
